import { createElement, replaceContent } from '@/lib/ui/renderer';
import { useStore } from '@/lib/state/store';
import type { Song } from '@/types';

export class QueueManager {
  private container: HTMLElement | null = null;
  private overlay: HTMLElement | null = null;
  private draggedIndex: number | null = null;

  constructor() {
    this.createUI();
  }

  private createUI(): void {
    // Overlay
    this.overlay = createElement('div', {
      class: 'queue-overlay',
      onclick: (e) => {
        if (e.target === this.overlay) {
          this.close();
        }
      },
    });

    // Queue drawer
    this.container = createElement('div', { class: 'queue-drawer' });

    // Header
    const header = createElement('div', { class: 'queue-header' });

    const title = createElement('h2', { class: 'queue-title' });
    title.textContent = 'Queue';
    header.appendChild(title);

    const closeBtn = createElement('button', {
      class: 'icon-btn',
      onclick: () => this.close(),
      'aria-label': 'Close queue',
    });
    closeBtn.innerHTML = `<svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>`;
    header.appendChild(closeBtn);

    this.container.appendChild(header);

    // Actions
    const actions = createElement('div', { class: 'queue-actions' });

    const clearBtn = createElement('button', {
      class: 'btn btn-secondary',
      onclick: () => this.clearQueue(),
    });
    clearBtn.textContent = 'Clear Queue';
    actions.appendChild(clearBtn);

    const saveBtn = createElement('button', {
      class: 'btn btn-primary',
      onclick: () => this.saveAsPlaylist(),
    });
    saveBtn.textContent = 'Save as Playlist';
    actions.appendChild(saveBtn);

    this.container.appendChild(actions);

    // Queue list
    const list = createElement('div', {
      class: 'queue-list',
      id: 'queue-list',
    });
    this.container.appendChild(list);

    this.overlay.appendChild(this.container);
    document.body.appendChild(this.overlay);
  }

  open(): void {
    if (!this.overlay) return;
    this.render();
    this.overlay.classList.add('open');
  }

  close(): void {
    if (!this.overlay) return;
    this.overlay.classList.remove('open');
  }

  toggle(): void {
    if (!this.overlay) return;
    if (this.overlay.classList.contains('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  private render(): void {
    const list = document.getElementById('queue-list');
    if (!list) return;

    const { queue, trackIndex } = useStore.getState();

    if (queue.length === 0) {
      replaceContent(
        list,
        createElement('div', {
          class: 'empty',
          innerHTML: `
            <div class="empty-icon">🎵</div>
            <div class="empty-title">Queue is empty</div>
            <div class="empty-text">Add songs to start playing</div>
          `,
        })
      );
      return;
    }

    list.innerHTML = '';

    queue.forEach((song, index) => {
      const item = this.renderQueueItem(song, index, index === trackIndex);
      list.appendChild(item);
    });
  }

  private renderQueueItem(song: Song, index: number, isCurrent: boolean): HTMLElement {
    const item = createElement('div', {
      class: isCurrent ? 'queue-item current' : 'queue-item',
      draggable: true,
      'data-index': index.toString(),
    });

    // Drag handle
    const handle = createElement('div', { class: 'drag-handle' });
    handle.innerHTML = `<svg viewBox="0 0 24 24" width="20" height="20"><path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/></svg>`;
    item.appendChild(handle);

    // Song info
    const info = createElement('div', { class: 'queue-item-info' });

    if (song.thumbnail) {
      const thumb = createElement('img', {
        src: song.thumbnail,
        alt: song.title,
        class: 'queue-item-thumb',
      });
      info.appendChild(thumb);
    }

    const text = createElement('div', { class: 'queue-item-text' });
    const title = createElement('div', { class: 'queue-item-title' });
    title.textContent = song.title;
    text.appendChild(title);

    const artist = createElement('div', { class: 'queue-item-artist' });
    artist.textContent = song.artist;
    text.appendChild(artist);

    info.appendChild(text);
    item.appendChild(info);

    // Actions
    const actions = createElement('div', { class: 'queue-item-actions' });

    if (isCurrent) {
      const nowPlaying = createElement('span', { class: 'now-playing-badge' });
      nowPlaying.textContent = 'Now Playing';
      actions.appendChild(nowPlaying);
    }

    const removeBtn = createElement('button', {
      class: 'icon-btn',
      onclick: (e) => {
        e.stopPropagation();
        this.removeFromQueue(index);
      },
      'aria-label': 'Remove from queue',
    });
    removeBtn.innerHTML = `<svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>`;
    actions.appendChild(removeBtn);

    item.appendChild(actions);

    // Drag events
    item.addEventListener('dragstart', (e) => this.handleDragStart(e, index));
    item.addEventListener('dragend', (e) => this.handleDragEnd(e));
    item.addEventListener('dragover', (e) => this.handleDragOver(e));
    item.addEventListener('drop', (e) => this.handleDrop(e, index));

    // Click to play
    info.addEventListener('click', () => {
      const store = useStore.getState();
      if (store.queue[index]) {
        store.setCurrentTrack(store.queue[index]!, index);
        // playerManager would handle actual playback
      }
    });

    return item;
  }

  private handleDragStart(e: DragEvent, index: number): void {
    this.draggedIndex = index;
    const target = e.currentTarget as HTMLElement;
    target.classList.add('dragging');
    e.dataTransfer!.effectAllowed = 'move';
  }

  private handleDragEnd(e: DragEvent): void {
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('dragging');

    // Remove all drop-over classes
    document.querySelectorAll('.queue-item').forEach((item) => {
      item.classList.remove('drop-over');
    });
  }

  private handleDragOver(e: DragEvent): void {
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'move';

    const target = (e.currentTarget as HTMLElement).closest('.queue-item') as HTMLElement;
    if (target) {
      target.classList.add('drop-over');
    }
  }

  private handleDrop(e: DragEvent, dropIndex: number): void {
    e.preventDefault();
    e.stopPropagation();

    if (this.draggedIndex === null || this.draggedIndex === dropIndex) {
      return;
    }

    const store = useStore.getState();
    const newQueue = [...store.queue];
    const [draggedItem] = newQueue.splice(this.draggedIndex, 1);

    if (draggedItem) {
      newQueue.splice(dropIndex, 0, draggedItem);

      // Update track index if needed
      let newIndex = store.trackIndex;
      if (this.draggedIndex === store.trackIndex) {
        newIndex = dropIndex;
      } else if (
        this.draggedIndex < store.trackIndex &&
        dropIndex >= store.trackIndex
      ) {
        newIndex--;
      } else if (
        this.draggedIndex > store.trackIndex &&
        dropIndex <= store.trackIndex
      ) {
        newIndex++;
      }

      store.setQueue(newQueue, newIndex);
      this.render();
    }

    this.draggedIndex = null;
  }

  private removeFromQueue(index: number): void {
    const store = useStore.getState();
    store.removeFromQueue(index);
    this.render();
  }

  private clearQueue(): void {
    if (confirm('Clear the entire queue?')) {
      const store = useStore.getState();
      store.clearQueue();
      this.render();
    }
  }

  private saveAsPlaylist(): void {
    const name = prompt('Enter playlist name:');
    if (!name) return;

    const store = useStore.getState();
    const playlist = {
      id: crypto.randomUUID(),
      name: name.trim(),
      songs: store.queue,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    store.addPlaylist(playlist);
    alert('Playlist saved!');
  }
}

// Export singleton
export const queueManager = new QueueManager();
